import { useMemo } from "react";
import { useRouter } from "next/router";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useAppContext } from "@context/state";
import { useAbility } from "@hooks/useAbility";
import { ABILITY } from "@constants/abilities";
import { formatDollarAmount } from "@utils/costFormatter";
import { getBudgetBreakdown } from "@utils/homeDashboard";
import InsightCard from "./InsightCard";

const BUDGET_REASON =
  "We use your budget to determine affordability grades for the colleges on your list. Your budget is also critical for spotting detrimental funding gaps to know about before the first tuition bill arrives.";

const BudgetSection = () => {
  const { scenario } = useAppContext();
  const { canEdit, isHidden } = useAbility();
  const router = useRouter();

  const budget = useMemo(
    () => getBudgetBreakdown(scenario?.preapprovals),
    [scenario?.preapprovals],
  );

  if (isHidden(ABILITY.HOME_BUDGET)) {
    return null;
  }

  const hasBudget = budget.total > 0;

  return (
    <section aria-labelledby="budget-heading" className="space-y-3">
      <h2 id="budget-heading" className="m-0 text-xl font-bold text-gray-900">
        Your Budget
      </h2>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="flex items-start justify-between gap-3 p-4">
          <div>
            {!hasBudget && (
              <p className="text-sm text-gray-500">
                Family’s yearly college budget
              </p>
            )}
            <p
              className={`text-3xl font-medium ${hasBudget ? "text-success" : "text-gray-900"}`}
            >
              {formatDollarAmount(budget.total)}
            </p>
            {hasBudget && (
              <p className="text-sm text-gray-500">
                Total estimated 4-year budget, about{" "}
                <strong className="text-gray-900">
                  {formatDollarAmount(Math.round(budget.yearly))}
                </strong>{" "}
                a year
              </p>
            )}
          </div>
          {canEdit(ABILITY.BUDGET) && (
            <button
              type="button"
              onClick={() => router.push("/profile?tab=my-college-budget")}
              aria-label="Edit your budget"
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
            >
              <EditOutlinedIcon sx={{ fontSize: 16 }} aria-hidden="true" />
              Edit
            </button>
          )}
        </div>

        {!isHidden(ABILITY.AFFORDABILITY_BUDGET_BREAKDOWN) && (
          <dl className="grid grid-cols-2 md:grid-cols-4 border-t border-gray-200">
            {budget.items.map((item, index) => (
              <div
                key={item.label}
                className={`p-4 ${index > 0 ? "md:border-l border-gray-200" : ""} ${index % 2 === 1 ? "border-l md:border-l" : ""} ${index > 1 ? "border-t md:border-t-0" : ""}`}
              >
                <dt className="text-xs text-gray-500">{item.label}</dt>
                <dd className="text-base font-medium text-gray-900">
                  {formatDollarAmount(item.value)}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>

      <InsightCard label="Why we ask for this" body={BUDGET_REASON} />
    </section>
  );
};

export default BudgetSection;
